import { useState } from "react";
import { Check, CheckCircle2, Loader2, MessageCircle, Send } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";
import { sendReportInterest } from "@/lib/api";
import { trackEvent } from "@/lib/track";
import { useSiteSettings, freeSlotsLine, slotsTemplate, eur } from "@/lib/siteSettings";
import CheckoutDialog from "./CheckoutDialog";
import { PreparingCard } from "./checkoutReturn";
import { T } from "./i18n";
import { S } from "./summaryCopy";
import { SALES, SENDER, whatsappLink } from "./salesConfig";

// The offer: what 39 € buys, in concrete items, with the delivery promise and
// the no-phone-call promise — and NOTHING that is not yet true. Payment wording
// and the refund promise sit behind SALES.paymentLive / SALES.guaranteeLive
// (see salesConfig.js); until then the button opens the existing interest form.
// Phase 2 replaces the form with checkout when paymentLive turns on.
export default function OfferBlock({ token, lang, findingTotal, purchaseStatus }) {
  const L = T[lang];
  const o = S[lang].offer;
  const settings = useSiteSettings();
  const price = eur(settings.report_price_eur, lang);
  const slotsLine = freeSlotsLine(slotsTemplate({ some: o.consultingFree, all: o.consultingFreeAll }, settings), settings);
  const paymentsLive = settings.payments_live === true;
  const [checkoutOpen, setCheckoutOpen] = useState(false);
  const [formOpen, setFormOpen] = useState(false);
  const [form, setForm] = useState({ name: "", contact: "", message: "" });
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(false);
  const setField = (f) => (e) => setForm((s) => ({ ...s, [f]: e.target.value }));

  const onCta = () => {
    trackEvent(token, "cta_clicked", { placement: "inline" });
    // Real checkout only when the backend says payments are live; otherwise the
    // existing request form (no fake checkout).
    if (paymentsLive) setCheckoutOpen(true);
    else setFormOpen(true);
  };

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await sendReportInterest(token, form);
      setSent(true);
      toast.success(L.interestSent);
    } catch {
      toast.error(L.interestFailed);
    } finally {
      setSending(false);
    }
  };

  // Already paid, report not delivered yet: status only, no price, no buy button.
  if (purchaseStatus === "paid") return <PreparingCard lang={lang} />;

  return (
    <div id="angebot" className="scroll-mt-4 rounded-2xl border border-primary/30 bg-accent/40 p-5 sm:p-6 print:hidden">
      <h2 className="font-heading text-xl font-bold">{o.title}</h2>
      <div className="mt-2 flex flex-wrap items-baseline gap-x-2">
        <span className="text-4xl font-bold tabular-nums">{price}</span>
        <span className="text-base text-muted-foreground">{o.priceNote}</span>
      </div>
      {findingTotal > 0 && <p className="mt-3 text-base">{o.lead(findingTotal)}</p>}

      <ul className="mt-4 space-y-2">
        {o.includes.map((item) => (
          <li key={item} className="flex items-start gap-2 text-base">
            <Check className="mt-1 h-4 w-4 shrink-0 text-primary" /> <span>{item}</span>
          </li>
        ))}
      </ul>

      <div className="mt-4 space-y-2 text-base text-muted-foreground">
        <p>{o.delivery(SALES.deliveryHours)}</p>
        <p className="font-medium text-foreground">{o.noCall}</p>
        {slotsLine && <p data-testid="offer-consulting-free" className="font-medium text-primary">{slotsLine}</p>}
        {SALES.paymentLive && <p>{o.payNote}</p>}
        {SALES.guaranteeLive && <p>{o.guarantee(price)}</p>}
      </div>

      {sent ? (
        <div className="mt-5 flex flex-col items-center gap-1.5 py-2 text-center text-base">
          <CheckCircle2 className="h-6 w-6 text-primary" />
          <p className="font-medium">{L.thankYou}</p>
        </div>
      ) : !formOpen ? (
        <Button size="lg" className="mt-5 w-full sm:w-auto" onClick={onCta} data-testid="offer-cta">
          {SALES.paymentLive || paymentsLive ? o.ctaPay(price) : o.ctaRequest(price)}
        </Button>
      ) : (
        <form onSubmit={submit} className="mt-5 max-w-sm space-y-2.5">
          {!SALES.paymentLive && <p className="text-base text-muted-foreground">{o.requestNote}</p>}
          <Input placeholder={L.formName} value={form.name} onChange={setField("name")} data-testid="report-request-name" />
          <Input placeholder={L.formContact} value={form.contact} onChange={setField("contact")} required data-testid="report-request-contact" />
          <Textarea placeholder={L.formMessage} rows={2} value={form.message} onChange={setField("message")} data-testid="report-request-message" />
          <Button type="submit" className="w-full" disabled={sending} data-testid="report-request-submit">
            {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
            {o.ctaRequest(price)}
          </Button>
        </form>
      )}

      {paymentsLive && (
        <CheckoutDialog open={checkoutOpen} onOpenChange={setCheckoutOpen} token={token} product="report" lang={lang} />
      )}

      <div className="mt-6 border-t pt-4">
        <h3 className="font-heading text-base font-semibold">{o.afterTitle}</h3>
        <p className="mt-1 text-base text-muted-foreground">{o.afterDesc}</p>
      </div>
    </div>
  );
}

export function FaqBlock({ lang }) {
  const { report_price_eur } = useSiteSettings();
  const items = S[lang]
    .faq(eur(report_price_eur, lang), SALES.deliveryHours)
    .filter((f) => f.requires !== "guarantee" || SALES.guaranteeLive);
  return (
    <Accordion type="single" collapsible className="mt-3 space-y-2 print:hidden">
      {items.map((f, i) => (
        <AccordionItem key={f.q} value={`faq-${i}`} className="rounded-xl border bg-card px-4">
          <AccordionTrigger className="text-left text-base font-medium">{f.q}</AccordionTrigger>
          <AccordionContent className="text-base text-muted-foreground">{f.a}</AccordionContent>
        </AccordionItem>
      ))}
    </Accordion>
  );
}

// Real identity or nothing — a "[Firmenname]" placeholder next to a 39 € offer
// destroys the trust the whole page is built to earn. The outreach dialog also
// refuses to send while the sender identity is incomplete (see MessageDialog).
export function SenderBlock({ lang }) {
  // Live flag from /api/site-settings; SALES.legalPagesLive is only the fallback.
  const { legal_live } = useSiteSettings();
  if (!SENDER.name) return null;
  const C = S[lang].sender;
  const wa = whatsappLink(SENDER.phone);
  return (
    <div className="mt-8 border-t pt-5 text-center text-sm text-muted-foreground">
      <p className="text-xs font-semibold uppercase tracking-wide">{C.title}</p>
      <p className="mt-1 font-medium text-foreground">
        {SENDER.company ? `${SENDER.name} · ${SENDER.company}` : SENDER.name}
      </p>
      <p className="mx-auto mt-1 max-w-md">{C.desc(SENDER.name)}</p>
      <div className="mt-2 flex flex-wrap items-center justify-center gap-x-4 gap-y-1">
        {wa && (
          <a href={wa} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 hover:text-primary">
            <MessageCircle className="h-3.5 w-3.5" /> WhatsApp
          </a>
        )}
        {SENDER.phone && <a href={`tel:${SENDER.phone}`} className="hover:text-primary">{SENDER.phone}</a>}
        {SENDER.email && <a href={`mailto:${SENDER.email}`} className="hover:text-primary">{SENDER.email}</a>}
      </div>
      {legal_live && (
        <div className="mt-2 flex justify-center gap-4 text-xs">
          <a href="/impressum" className="hover:text-primary">{C.imprint}</a>
          <a href="/agb" className="hover:text-primary">{C.terms}</a>
          <a href="/datenschutz" className="hover:text-primary">{C.privacy}</a>
        </div>
      )}
    </div>
  );
}
